import React, { useEffect, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, PlusIcon, XIcon, CheckIcon, SearchIcon, ChevronLeftIcon } from 'lucide-react';
interface AcceptanceFiltersProps {
  deduplication: 'deduplicated' | 'raw';
  onDeduplicationChange: (value: 'deduplicated' | 'raw') => void;
  successRateMetric: 'payment-success-rate' | 'authorization-rate';
  onSuccessRateMetricChange: (value: 'payment-success-rate' | 'authorization-rate') => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  cardCountries: string[];
  onCardCountriesChange: (countries: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  additionalFilters: string[];
  onAdditionalFiltersChange: (filters: string[]) => void;
}
type MoreFiltersView = 'menu' | 'input-method' | 'currency' | 'network-token-status' | 'retry-status' | 'postal-code-response' | 'cvc-response' | '3ds-outcome';
export function AcceptanceFilters({
  deduplication,
  onDeduplicationChange,
  successRateMetric,
  onSuccessRateMetricChange,
  cardTypes,
  onCardTypesChange,
  cardCountries,
  onCardCountriesChange,
  cardBrands,
  onCardBrandsChange,
  interactionTypes,
  onInteractionTypesChange,
  additionalFilters,
  onAdditionalFiltersChange
}: AcceptanceFiltersProps) {
  const {
    t
  } = useTranslation('analytics');
  const [showDeduplicationDropdown, setShowDeduplicationDropdown] = useState(false);
  const [showSuccessRateDropdown, setShowSuccessRateDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showCardCountryDropdown, setShowCardCountryDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showMoreFiltersDropdown, setShowMoreFiltersDropdown] = useState(false);
  const [moreFiltersView, setMoreFiltersView] = useState<MoreFiltersView>('menu');
  // Shared payment filters (internal state)
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [showPMCategoryDropdown, setShowPMCategoryDropdown] = useState(false);
  const [showPMMethodDropdown, setShowPMMethodDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  // Additional filter states
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [networkTokenStatuses, setNetworkTokenStatuses] = useState<string[]>([]);
  const [retryStatuses, setRetryStatuses] = useState<string[]>([]);
  const [postalCodeResponses, setPostalCodeResponses] = useState<string[]>([]);
  const [cvcResponses, setCvcResponses] = useState<string[]>([]);
  const [threeDSOutcomes, setThreeDSOutcomes] = useState<string[]>([]);
  const deduplicationRef = useRef<HTMLDivElement>(null);
  const successRateRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const cardCountryRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const moreFiltersRef = useRef<HTMLDivElement>(null);
  // Shared payment filter refs
  const pmCategoryRef = useRef<HTMLDivElement>(null);
  const pmMethodRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deduplicationRef.current && !deduplicationRef.current.contains(event.target as Node)) {
        setShowDeduplicationDropdown(false);
      }
      if (successRateRef.current && !successRateRef.current.contains(event.target as Node)) {
        setShowSuccessRateDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (cardCountryRef.current && !cardCountryRef.current.contains(event.target as Node)) {
        setShowCardCountryDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(event.target as Node)) {
        setShowMoreFiltersDropdown(false);
        setMoreFiltersView('menu');
      }
      if (pmCategoryRef.current && !pmCategoryRef.current.contains(event.target as Node)) {
        setShowPMCategoryDropdown(false);
      }
      if (pmMethodRef.current && !pmMethodRef.current.contains(event.target as Node)) {
        setShowPMMethodDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const availableAdditionalFilters = [{
    id: 'input-method',
    label: 'Input method'
  }, {
    id: 'currency',
    label: 'Currency'
  }, {
    id: 'network-token-status',
    label: 'Network token status'
  }, {
    id: 'retry-status',
    label: 'Retry status'
  }, {
    id: 'postal-code-response',
    label: 'Postal code response'
  }, {
    id: 'cvc-response',
    label: 'CVC response'
  }, {
    id: '3ds-outcome',
    label: '3DS outcome'
  }];
  // Card countries with Malaysia first, then alphabetically
  const cardCountryOptions = ['Malaysia', 'Afghanistan', 'Åland Islands', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Australia', 'Singapore', 'United Kingdom', 'United States'];
  // Currency options with MYR first, then alphabetically
  const currencyOptions = ['MYR - Malaysian Ringgit', 'AED - United Arab Emirates Dirham', 'AFN - Afghan Afghani', 'ALL - Albanian Lek', 'AMD - Armenian Dram', 'ANG - Netherlands Antillean Guilder', 'AOA - Angolan Kwanza', 'ARS - Argentine Peso', 'AUD - Australian Dollar', 'SGD - Singapore Dollar', 'USD - US Dollar'];
  const handleMoreFilterClick = (filterId: string) => {
    setMoreFiltersView(filterId as MoreFiltersView);
  };
  const handleBackToMenu = () => {
    setMoreFiltersView('menu');
  };
  const handleApplyFilter = (applyFn: () => void) => {
    applyFn();
    setShowMoreFiltersDropdown(false);
    setMoreFiltersView('menu');
  };
  return <>
      {/* Main Filters - Same row as date/granularity */}
      <div className="relative" ref={deduplicationRef} data-id="element-588">
        <button onClick={() => setShowDeduplicationDropdown(!showDeduplicationDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-589">
          <span className="capitalize" data-id="element-590">{deduplication}</span>
          <ChevronDownIcon size={16} data-id="element-591" />
        </button>

        {showDeduplicationDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-592">
            <button onClick={() => {
          onDeduplicationChange('deduplicated');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'deduplicated' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-593">
              <div className="flex-1 min-w-0" data-id="element-594">
                <div className="text-sm font-medium text-gray-900" data-id="element-595">
                  {t('components.AcceptanceFilters.deduplicated')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-596">
                  {t('components.AcceptanceFilters.only_includes_the_final_outcome_of')}
                </div>
              </div>
              {deduplication === 'deduplicated' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-597" />}
            </button>

            <button onClick={() => {
          onDeduplicationChange('raw');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'raw' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-598">
              <div className="flex-1 min-w-0" data-id="element-599">
                <div className="text-sm font-medium text-gray-900" data-id="element-600">
                  {t('components.AcceptanceFilters.raw')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-601">
                  {t('components.AcceptanceFilters.includes_all_payment_attempts_shows_repeat')}
                </div>
              </div>
              {deduplication === 'raw' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-602" />}
            </button>
          </div>}
      </div>

      <div className="relative" ref={successRateRef} data-id="element-603">
        <button onClick={() => setShowSuccessRateDropdown(!showSuccessRateDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-604">
          <span data-id="element-605">
            {successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate'}
          </span>
          <ChevronDownIcon size={16} data-id="element-606" />
        </button>

        {showSuccessRateDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-607">
            <button onClick={() => {
          onSuccessRateMetricChange('payment-success-rate');
          setShowSuccessRateDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${successRateMetric === 'payment-success-rate' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-608">
              <div className="flex-1 min-w-0" data-id="element-609">
                <div className="text-sm font-medium text-gray-900" data-id="element-610">
                  {t('components.AcceptanceFilters.payment_success_rate')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-611">
                  {t('components.AcceptanceFilters.includes_attempted_payments_blocked_payments_and')}
                </div>
              </div>
              {successRateMetric === 'payment-success-rate' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-612" />}
            </button>

            <button onClick={() => {
          onSuccessRateMetricChange('authorization-rate');
          setShowSuccessRateDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${successRateMetric === 'authorization-rate' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-613">
              <div className="flex-1 min-w-0" data-id="element-614">
                <div className="text-sm font-medium text-gray-900" data-id="element-615">
                  {t('components.AcceptanceFilters.authorization_rate')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-616">
                  {t('components.AcceptanceFilters.excludes_blocked_payments_and_failed_authentications')}
                </div>
              </div>
              {successRateMetric === 'authorization-rate' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-617" />}
            </button>
          </div>}
      </div>

      {/* Secondary Filters - Separate row with smaller buttons */}
      <div className="w-full flex flex-wrap items-center gap-2" data-id="element-618">
        {/* Payment method category */}
        <div className="relative" ref={pmCategoryRef} data-id="element-619">
          <button onClick={() => setShowPMCategoryDropdown(!showPMCategoryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-620">
            <PlusIcon size={14} data-id="element-621" />
            <span data-id="element-622">
              {t('components.AcceptanceFilters.payment_method_category')}
            </span>
            {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-623">
                {paymentMethodCategories.length}
              </span>}
          </button>
          {showPMCategoryDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_category')} options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
          setPaymentMethodCategories(selected);
          setShowPMCategoryDropdown(false);
        }} onBack={null} data-id="element-624" />}
        </div>

        {/* Payment method */}
        <div className="relative" ref={pmMethodRef} data-id="element-625">
          <button onClick={() => setShowPMMethodDropdown(!showPMMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-626">
            <PlusIcon size={14} data-id="element-627" />
            <span data-id="element-628">{t('components.AcceptanceFilters.payment_method')}</span>
            {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-629">
                {paymentMethods.length}
              </span>}
          </button>
          {showPMMethodDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_payment_method')} options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
          setPaymentMethods(selected);
          setShowPMMethodDropdown(false);
        }} onBack={null} data-id="element-630" />}
        </div>

        {/* Funding source type */}
        <div className="relative" ref={fundingSourceRef} data-id="element-631">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-632">
            <PlusIcon size={14} data-id="element-633" />
            <span data-id="element-634">{t('components.AcceptanceFilters.funding_source_type')}</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-635">
                {fundingSourceTypes.length}
              </span>}
          </button>
          {showFundingSourceDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_funding_source')} options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          setFundingSourceTypes(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-636" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-637">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-638">
            <PlusIcon size={14} data-id="element-639" />
            <span data-id="element-640">{t('components.AcceptanceFilters.card_type')}</span>
          </button>

          {showCardTypeDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_type')} options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-641" />}
        </div>

        <div className="relative" ref={cardCountryRef} data-id="element-642">
          <button onClick={() => setShowCardCountryDropdown(!showCardCountryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-643">
            <PlusIcon size={14} data-id="element-644" />
            <span data-id="element-645">{t('components.AcceptanceFilters.card_country')}</span>
          </button>

          {showCardCountryDropdown && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_country')} options={cardCountryOptions} selectedOptions={cardCountries} onApply={selected => {
          onCardCountriesChange(selected);
          setShowCardCountryDropdown(false);
        }} data-id="element-646" />}
        </div>

        <div className="relative" ref={cardBrandRef} data-id="element-647">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-648">
            <PlusIcon size={14} data-id="element-649" />
            <span data-id="element-650">{t('components.AcceptanceFilters.card_brand')}</span>
          </button>

          {showCardBrandDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_brand')} options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-651" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-652">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-653">
            <PlusIcon size={14} data-id="element-654" />
            <span data-id="element-655">{t('components.AcceptanceFilters.interaction_type')}</span>
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_interaction_type')} options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-656" />}
        </div>

        <div className="relative" ref={moreFiltersRef} data-id="element-657">
          <button onClick={() => {
          setShowMoreFiltersDropdown(!showMoreFiltersDropdown);
          setMoreFiltersView('menu');
        }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-658">
            <PlusIcon size={14} data-id="element-659" />
            <span data-id="element-660">{t('components.AcceptanceFilters.more_filters')}</span>
          </button>

          {showMoreFiltersDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-661">
              {moreFiltersView === 'menu' && <>
                  {availableAdditionalFilters.map(filter => <button key={filter.id} onClick={() => handleMoreFilterClick(filter.id)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-662">
                      <PlusIcon size={14} className="text-blue-600" data-id="element-663" />
                      <span data-id="element-664">{filter.label}</span>
                    </button>)}
                </>}

              {moreFiltersView === 'input-method' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_input_method')} options={['In-person (card present)', 'Online (card-not-present)', 'Apple Pay', 'Google Pay']} selectedOptions={inputMethods} onApply={selected => handleApplyFilter(() => setInputMethods(selected))} onBack={handleBackToMenu} isInline data-id="element-665" />}

              {moreFiltersView === 'currency' && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_currency')} options={currencyOptions} selectedOptions={currencies} onApply={selected => handleApplyFilter(() => setCurrencies(selected))} onBack={handleBackToMenu} isInline data-id="element-666" />}

              {moreFiltersView === 'network-token-status' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_network_token_status')} options={['Non-tokenized card', 'Network token']} selectedOptions={networkTokenStatuses} onApply={selected => handleApplyFilter(() => setNetworkTokenStatuses(selected))} onBack={handleBackToMenu} isInline data-id="element-667" />}

              {moreFiltersView === 'retry-status' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_retry_status')} options={['Initial attempt', 'Retry']} selectedOptions={retryStatuses} onApply={selected => handleApplyFilter(() => setRetryStatuses(selected))} onBack={handleBackToMenu} isInline data-id="element-668" />}

              {moreFiltersView === 'postal-code-response' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_postal_code_response')} options={['Not sent for verification', 'Verified', 'Failed verification', 'Unchecked']} selectedOptions={postalCodeResponses} onApply={selected => handleApplyFilter(() => setPostalCodeResponses(selected))} onBack={handleBackToMenu} isInline data-id="element-669" />}

              {moreFiltersView === 'cvc-response' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_cvc_response')} options={['Not sent', 'Passed', 'Failed', 'Unchecked']} selectedOptions={cvcResponses} onApply={selected => handleApplyFilter(() => setCvcResponses(selected))} onBack={handleBackToMenu} isInline data-id="element-670" />}

              {moreFiltersView === '3ds-outcome' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_3ds_outcome')} options={['Successful 3DS frictionless flow', 'Successful 3DS challenge flow', 'Failed 3DS challenge', '3DS unavailable', '3DS not actioned', 'No 3DS']} selectedOptions={threeDSOutcomes} onApply={selected => handleApplyFilter(() => setThreeDSOutcomes(selected))} onBack={handleBackToMenu} isInline data-id="element-671" />}
            </div>}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-672">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-673">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-674" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-675">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-676">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-677">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-678" />

            <span className="text-base text-gray-900" data-id="element-679">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-680">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-681">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-682">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-683">
      {content}
    </div>;
}
// Searchable Dropdown Component (for card country and currency)
interface SearchableDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack?: (() => void) | null;
  isInline?: boolean;
}
function SearchableDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: SearchableDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200" data-id="element-684">
        <div className="flex items-center gap-3 mb-2" data-id="element-685">
          {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-686">
              <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-687" />
            </button>}
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-688">{title}</h3>
        </div>
        <div className="relative" data-id="element-689">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-690" />

          <input type="search" aria-label={t('components.content.attrs.search')} placeholder={t('components.content.attrs.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-691" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-692">
        {filteredOptions.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-693">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-694" />

            <span className="text-sm text-gray-900" data-id="element-695">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-696">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-697">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-698">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-699">
      {content}
    </div>;
}